import {
  RadioButtonGroupMapping,
  RadioButtonType,
} from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import {
  Dimension,
  EntitlementOption,
  PriceTypes,
} from '../../../modules/privateOffer/types'
import type { Product } from '../../../modules/productsListing/types'

export type IndexedDimension = {
  index: number
  dimension: Dimension
}

export const subscriptionTableMapping = {
  NAME: 'name',
  DESCRIPTION: 'description',
  RATES: 'rates',
}

export const dimensionSelectionMapping: RadioButtonGroupMapping[] = [
  {
    key: EntitlementOption.SINGLE,
    label: 'Buyer can choose only one of the existing dimensions',
    type: RadioButtonType.STANDARD,
  },
  {
    key: EntitlementOption.MANY,
    label: 'Buyer can choose one or more existing dimensions',
    type: RadioButtonType.STANDARD,
  },
]

export const priceMapping = {
  price1m: '1 Month',
  price12m: '12 Month',
  price24m: '24 Month',
  price36m: '36 Month',
}

export const getOfferIds = (dimensions: Dimension[]) =>
  dimensions
    .map(dimension => dimension.dimensionId || '')
    .filter(id => !isEmpty(id))

export const getStandardProductDimensions = (product: Product) =>
  product?.productDimensions?.filter(
    dimension => dimension.isAdditionalUsageAllowed === false
  ) || []

export const getDimensionPrice = (
  dimension: Dimension,
  isResale: boolean
): PriceTypes =>
  isResale
    ? ({
        ...(dimension?.prices as PriceTypes),
        price: dimension?.price,
      } as PriceTypes)
    : ({
        price: dimension?.price ?? null,
      } as PriceTypes)

export const getCustomDimensionForSubscription = (
  dimension: Dimension,
  index: number,
  key: string,
  isResale: boolean,
  dimensionsError: Record<string, unknown>[]
) => {
  if (isResale) {
    return {
      value: get(dimension, `prices[${key}]`, null),
      errorText: (dimensionsError[index]?.subscriptionPrice as string) || '',
    }
  } else {
    return {
      value: get(dimension, 'price', null),
      errorText: (dimensionsError[index]?.price as string) || '',
    }
  }
}

export const getCustomDimensions = (
  dimensions: Dimension[],
  isResale: boolean
) =>
  dimensions.reduce(
    (
      customDimensions: IndexedDimension[],
      dimension: Dimension,
      index: number
    ) => {
      if (dimension.type === 'custom') {
        customDimensions.push({
          index,
          dimension: {
            ...dimension,
            price: getDimensionPrice(dimension, isResale),
            id: dimension.dimensionId || '',
          },
        })
      }
      return customDimensions
    },
    []
  )

export const getStandardDimensions = (dimensions: Dimension[]) =>
  dimensions.reduce(
    (
      standardDimensions: IndexedDimension[],
      dimension: Dimension,
      index: number
    ) => {
      if (dimension.type === 'standard') {
        standardDimensions.push({ index, dimension })
      }
      return standardDimensions
    },
    []
  )

export const getAdditionalUsageDimensions = (dimensions: Dimension[]) =>
  dimensions.reduce(
    (
      additionalUsageDimensions: IndexedDimension[],
      dimension: Dimension,
      index: number
    ) => {
      if (
        dimension.isAdditionalUsageAllowed ||
        dimension.type === 'additional_usage'
      ) {
        additionalUsageDimensions.push({ index, dimension })
      }
      return additionalUsageDimensions
    },
    []
  )

export const getCustomDimensionPriceMappings = (isCustom?: boolean) => {
  if (isCustom) {
    return { ...priceMapping, price: 'Custom Price' }
  } else {
    return { ...priceMapping, price: 'Price' }
  }
}

export const getStandardDimensionPriceMappings = (isCustom?: boolean) => {
  if (isCustom) {
    return { ...priceMapping, price: 'Custom price' }
  } else {
    return { ...priceMapping, price: 'Price' }
  }
}

export const getAdditionalDimensionPriceKey = (isResale: boolean) =>
  isResale ? 'additionalUsagePrice' : 'price'

export const getAdditionalDimensionValue = (
  additionalDimension: Dimension,
  isResale: boolean
) =>
  (isResale
    ? get(additionalDimension, 'prices[additionalUsagePrice]', null)
    : additionalDimension?.price) ?? ''
